/* global BigInt */

// The wire model is signed integer units + asset + scale. Never coerce units to Number.
export function normalizeMoney(value) {
  if (!value || typeof value.units !== 'string' || !/^(0|-?[1-9]\d*)$/.test(value.units)
    || typeof value.asset !== 'string' || !value.asset.trim()
    || !Number.isInteger(value.scale) || value.scale < 0 || value.scale > 36) return null;
  return { units: value.units, asset: value.asset, scale: value.scale };
}

function currencyCode(value) {
  return typeof value === 'string' ? value.trim() : typeof value?.code === 'string' ? value.code.trim() : '';
}

export function currencyScale(asset) {
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency: asset }).resolvedOptions().maximumFractionDigits;
  } catch {
    return 2;
  }
}

export function moneyFromDecimal(value, currency) {
  const asset = currencyCode(currency);
  if (!asset || (typeof value !== 'string' && !Number.isSafeInteger(value))) return null;
  const raw = String(value).trim();
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return null;
  const [whole, fraction = ''] = raw.split('.');
  if (fraction.length > 36) return null;
  const units = BigInt(whole + fraction).toString();
  return normalizeMoney({ units, asset, scale: fraction.length });
}

export function moneyFromMinor(value, currency) {
  const asset = currencyCode(value?.currency) || currencyCode(currency);
  const amount = value?.amount;
  if (!asset || (typeof amount !== 'string' && !Number.isSafeInteger(amount)) || !/^-?\d+$/.test(String(amount))) return null;
  return normalizeMoney({ units: BigInt(amount).toString(), asset, scale: currencyScale(asset) });
}

// Compatibility lives here: components receive one exact representation, never raw decimals.
export function getAccountMoney(account, field = 'balance') {
  const sync = account?.tpAccountSyncDTO ?? account?.tpAccountSync ?? account?.tp_account_sync_dto ?? account?.tp_account_sync ?? account?.sync;
  if (sync?.money && Object.prototype.hasOwnProperty.call(sync.money, field)) return normalizeMoney(sync.money[field]);
  const legacyKey = field.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
  const value = sync?.[field] ?? sync?.[legacyKey];
  if (value && typeof value === 'object') return normalizeMoney(value) || moneyFromMinor(value, account?.currency);
  return moneyFromDecimal(value, account?.currency);
}

export function formatMoney(value, locale, { fallback = '—', trimTrailingZeros = false, style = 'decimal', currencyDisplay = 'symbol' } = {}) {
  const money = normalizeMoney(value);
  if (!money) return fallback;
  const negative = money.units.startsWith('-');
  const digits = money.units.replace(/^-/, '').padStart(money.scale + 1, '0');
  const whole = BigInt(money.scale ? digits.slice(0, -money.scale) : digits);
  let fraction = money.scale ? digits.slice(-money.scale) : '';
  if (trimTrailingZeros) fraction = fraction.replace(/0+$/, '').padEnd(currencyScale(money.asset), '0');
  const options = {
    minimumFractionDigits: fraction ? 1 : 0,
    maximumFractionDigits: fraction ? 1 : 0,
  };
  let formatter;
  let suffix = ` ${money.asset}`;
  if (style === 'currency') {
    try {
      formatter = new Intl.NumberFormat(locale, { ...options, style: 'currency', currency: money.asset, currencyDisplay });
      suffix = '';
    } catch { /* Assets without an ISO currency code use the exact decimal + asset display. */ }
  }
  formatter ||= new Intl.NumberFormat(locale, options);
  const digitFormatter = new Intl.NumberFormat(locale, { useGrouping: false });
  const localizedFraction = fraction.replace(/\d/g, digit => digitFormatter.format(Number(digit)));
  // -0 retains the locale's sign and direction marks for negative subunit balances.
  const signedWhole = negative ? (whole === BigInt(0) ? -0 : -whole) : whole;
  return formatter.formatToParts(signedWhole).map(part => part.type === 'fraction' ? localizedFraction : part.value).join('') + suffix;
}
